import { VStack, Spinner, Box, HStack, Text, Input, Pressable, Badge, useMediaQuery } from "native-base";
import { useEffect, useState } from "react";
import CryptoJS from "crypto-js";
import CustomModal from "../../../../components/modal";
import CustomButton from "../../../../components/button";
import MobileModelSheet from "../../../../components/actionSheet";
import NoDataFound from "../../../../components/noDataFound";
import { getEventRequestedUsersListingAPI, updateUserEventAccessRequest } from "../../../../api/apis/workflow";
import { Toaster } from "../../../../components/toaster";
import { LoaderAnimation } from "../../../../components/spinner";
import { COLORS } from "../../../../utils/colors";
import { COMMONFONTSIZE } from "../../../../utils/fonts";
import { MOBILE_VIEW_WIDTH } from "../../../../utils/screens";
import config from "../../../../utils/config";

import Svg from 'react-native-svg';

const UserAccessRequests = () => {
    const bytes = CryptoJS.AES.decrypt(
        sessionStorage.getItem("content"),
        config.userKey
      );
      const jsonString = bytes.toString(CryptoJS.enc.Utf8);
      const userInfo = JSON.parse(jsonString);
  const toaster = Toaster();
  const [MobileWindow] = useMediaQuery({ maxWidth: MOBILE_VIEW_WIDTH });

  // Users listing states
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [pagination, setPagination] = useState(null);

  // Confirmation modal states
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionType, setActionType] = useState(''); // 'approve' or 'reject'
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    getUsersList(1, false);
  }, []);

  useEffect(() => {
    // Set loading to false after users data is fetched (whether empty or not)
    if (!isLoadingUsers) {
      setIsLoading(false);
    }
  }, [isLoadingUsers]);

  // Users listing functions
  const getUsersListWithParams = async (page = 1, append = false, search = null) => {
    setIsLoadingUsers(true);
    try {
      // Use provided parameters or fall back to state values
      const searchParam = search !== null ? search : searchQuery.trim();
      
      const response = await getEventRequestedUsersListingAPI(page, 10, searchParam);
      
      if (response.error) {
        toaster(response.message || 'Failed to fetch users', 'error', 'error');
        return;
      }

      // Handle the actual response structure based on the API response
      const userData = response.data?.data?.data || [];
      const paginationData = response.data?.data?.pagination || null;

      if (append) {
        setUsers(prev => [...prev, ...userData]);
      } else {
        setUsers(userData);
      }

      // Update pagination state
      setPagination(paginationData);
      if (paginationData) {
        setHasMore(paginationData.page < paginationData.pages && userData.length > 0);
      } else {
        setHasMore(userData.length > 0);
      }

    } catch (error) {
      console.error('Error fetching users:', error);
      toaster('Error fetching users', 'error', 'error');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const getUsersList = async (page = 1, append = false) => {
    await getUsersListWithParams(page, append, null);
  };

  // Approve/Reject functionality
  const handleApproveClick = (user) => {
    setSelectedUser(user);
    setActionType('approve');
    setShowConfirmModal(true);
  };

  const handleRejectClick = (user) => {
    setSelectedUser(user);
    setActionType('reject');
    setShowConfirmModal(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedUser || !actionType) return;
    
    setIsProcessing(true);
    LoaderAnimation.show();
    
    try {
      const access_for_event = actionType === 'approve' ? 'ACCESS_APPROVED' : 'ACCESS_REJECTED';
      const response = await updateUserEventAccessRequest(selectedUser?.user_id, selectedUser?.event_id, access_for_event);
      
      if (response.error) {
        toaster(response.message || `Failed to ${actionType} user access request`, 'error', 'error');
        return;
      }
      
      toaster(response?.data?.data?.message || `User access request ${actionType}d successfully`, 'success', 'success');
      setShowConfirmModal(false);
      setSelectedUser(null);
      setActionType('');
      
      // Refresh the user list
      getUsersList(1, false);
    } catch (error) {
      console.error(`Error ${actionType}ing user access request:`, error);
      toaster(`Error ${actionType}ing user access request`, 'error', 'error');
    } finally {
      setIsProcessing(false);
      LoaderAnimation.hide();
    }
  };

  const closeConfirmModal = () => {
    setShowConfirmModal(false);
    setSelectedUser(null);
    setActionType('');
  };

  const handleSearch = () => {
    setCurrentPage(1);
    getUsersListWithParams(1, false, searchQuery.trim());
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setCurrentPage(1);
    getUsersListWithParams(1, false, '');
  };

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    getUsersList(nextPage, true);
  };

  // Clear icon SVG
  const clearIconSVG = (
    <path
      d='M18 6L6 18M6 6L18 18'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  );

  // Confirmation modal body
  const getConfirmModalBody = () => {
    return (
      <Box justifyContent="center" alignItems="center">
        <Text
          fontSize={COMMONFONTSIZE.EXTRA_LARGE_FONT_SIZE}
          fontWeight="medium"
          textAlign="center"
        >
          Are you sure you want to {actionType} the access request for{' '}
          <Text fontWeight="bold">
            {selectedUser?.display_name || `${selectedUser?.first_name || ''} ${selectedUser?.last_name || ''}`.trim()}
          </Text>?
        </Text>
      </Box>
    );
  };

  // Confirmation modal footer
  const getConfirmModalFooter = () => {
    return (
      <HStack justifyContent="flex-end" width="100%" space={4}>
        <CustomButton
          text="Cancel"
          onPress={closeConfirmModal}
          isOutline
          bgColor={COLORS.BLACK_COLOR}
          textColor={COLORS.BLACK_COLOR}
        />
        <CustomButton
          text={isProcessing ? `${actionType === 'approve' ? 'Approving' : 'Rejecting'}...` : `Confirm ${actionType === 'approve' ? 'Approve' : 'Reject'}`}
          onPress={handleConfirmAction}
          isLoading={isProcessing}
          isOutline
          bgColor={actionType === 'approve' ? "green.500" : COLORS.REJECT_TEXT_COLOR}
          textColor={actionType === 'approve' ? "green.500" : COLORS.REJECT_TEXT_COLOR}
        />
      </HStack>
    );
  };

  return (
    <>
      {isLoading ? (
        <Box flex={1} alignItems="center" justifyContent="center">
          <Spinner color={COLORS.THEME_COLOR} size="lg" />
        </Box>
      ) : (
        <Box>
          {/* Users Listing Section */}
          {(users?.length > 0 || searchQuery.trim()) && (
            <HStack justifyContent={'end'} alignItems={'center'} mb={4}>
              <HStack space={4} alignItems={'center'} flexWrap={MobileWindow ? 'wrap' : 'nowrap'}>
                {/* Search Input with Clear Icon */}
                <Box position='relative'>
                  <Input
                    placeholder='Search users...'
                    value={searchQuery}
                    onChangeText={(text) => {
                      // Only remove leading spaces, preserve trailing and middle spaces
                      const trimmedText = text?.trimStart();
                      setSearchQuery(trimmedText);
                      // If user clears the input with backspace, trigger search immediately
                      if (!trimmedText) {
                        setCurrentPage(1);
                        getUsersListWithParams(1, false, '');
                      }
                    }}
                    onKeyPress={handleKeyPress}
                    isLoading={isLoadingUsers}
                    width={MobileWindow ? '200px' : '300px'}
                    border={0}
                    bg={COLORS.WHITE_COLOR}
                    _focus={{ borderColor: 'transparent' }}
                    fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                    paddingRight={searchQuery ? '40px' : '12px'}
                  />
                  {searchQuery && (
                    <Pressable
                      position='absolute'
                      right={2}
                      top={0}
                      bottom={0}
                      justifyContent='center'
                      alignItems='center'
                      onPress={handleClearSearch}
                      p={1}
                      borderRadius={4}
                      _pressed={{ bg: COLORS.BORDER_COLOR }}
                    >
                      <Svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
                        {clearIconSVG}
                      </Svg>
                    </Pressable>
                  )}
                </Box>
              </HStack>
            </HStack>
          )}

          {/* Users Table */}
          {users?.length > 0 ? (
            <Box bg={COLORS.WHITE_COLOR} borderRadius={8} borderWidth={1} borderColor={COLORS.BORDER_COLOR} overflow="hidden">
              <Box overflowX="auto">
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: COLORS.BADGE_GRAY_COLOR }}>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Name</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>First Name</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Last Name</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Email</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Company</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Event Name</th>
                      <th style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user, index) => (
                      <tr key={user?.user_id || index} style={{ borderBottom: `1px solid ${COLORS.BORDER_COLOR}` }}>
                        <td style={{ padding: '12px' }}>
                          {user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'N/A'}
                        </td>
                        <td style={{ padding: '12px' }}>{user.first_name || 'N/A'}</td>
                        <td style={{ padding: '12px' }}>{user.last_name || 'N/A'}</td>
                        <td style={{ padding: '12px' }}>{user.email || 'N/A'}</td>
                        <td style={{ padding: '12px' }}>{user?.company || 'N/A'}</td>
                        <td style={{ padding: '12px' }}>{user?.event_name || 'N/A'}</td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <HStack space={2} justifyContent="center">
                            <CustomButton
                              text="Reject"
                              onPress={() => handleRejectClick(user)}
                              bgColor="transparent"
                              textColor={COLORS.REJECT_TEXT_COLOR}
                              fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                            />
                            <CustomButton
                              text="Approve"
                              onPress={() => handleApproveClick(user)}
                              bgColor="transparent"
                              textColor={COLORS.SUCCESS_TOAST_COLOR}
                              fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                            />
                          </HStack>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>

              {isLoadingUsers && (
                <Box p={8} textAlign="center">
                  <Spinner color={COLORS.THEME_COLOR} size="lg" />
                </Box>
              )}
            </Box>
          ) : users?.length === 0 && !isLoadingUsers ? (
            <NoDataFound dataName="access requests" />
          ) : null}

            {/* Pagination Info */}
            {pagination && users.length > 0 && (
              <Box mt={4} textAlign="center">
                <Text fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE} color={COLORS.PARTNERS_HEADING_COLOR}>
                  Page {pagination.page} of {pagination.pages} • Total {pagination.total} users
                </Text>
              </Box>
            )}

            {/* Load More Button */}
            {hasMore && users.length > 0 && !isLoadingUsers && (
              <Box mt={4} display="flex" justifyContent="center" alignSelf={'center'}>
                <CustomButton
                  text='Load More'
                  onPress={handleLoadMore}
                  isLoading={isLoadingUsers}
                />
              </Box>
            )}
          </Box>
      )}

      {/* Confirmation Modal */}
      {!MobileWindow ? (
        <CustomModal
          isOpen={showConfirmModal}
          onClose={closeConfirmModal}
          header={`${actionType === 'approve' ? 'Approve' : 'Reject'} Access Request`}
          body={getConfirmModalBody()}
          footer={getConfirmModalFooter()}
          width="max-content"
        />
      ) : (
        <MobileModelSheet
          isOpen={showConfirmModal}
          onClose={closeConfirmModal}
          header={`${actionType === 'approve' ? 'Approve' : 'Reject'} Access Request`}
          body={getConfirmModalBody()}
          footer={getConfirmModalFooter()}
          maxH="80vh"
        />
      )}
    </>
  );
};

export default UserAccessRequests;
